import Icon, {
  ExclamationCircleFilled,
  Loading3QuartersOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { useRequest } from 'ahooks';
import { App, message } from 'antd';
import classNames from 'classnames';
import { useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';

import ChatEmptySvg from '@/components/Icon/svgs/chat-empty.svg';
import CloseSvg from '@/components/Icon/svgs/close.svg';
import DeleteSvg from '@/components/Icon/svgs/delete.svg';
import NoteSvg from '@/components/Icon/svgs/note_fill.svg';
import SensorSvg from '@/components/Icon/svgs/sensors.svg';
import DoneIcon from '@/components/Icon/svgs/todo_done.svg';
import UndoneIcon from '@/components/Icon/svgs/todo_undone.svg';
import { HighLight } from '@/components/highLight';
import useGenius from '@/hooks/useGenius';
import { LAYOUT_SIZE } from '@/hooks/useLayoutSize';
import useStorage from '@/hooks/useStorage';
import { useTranslation } from '@/i18n/client';
import { EMemoryType, PROCESS_ENUM, deleteNote, relearnNote } from '@/service/note';
import type { INote, INote_List, IhighLight } from '@/service/note';
import useNoteItemStore from '@/store/useNoteItemStore';
import SimplyMD from '@/ui/components/memory-detail/components/attachments/components/SimplyMD';
import { isProduct } from '@/utils/host';
import { generateTimeString } from '@/utils/timestamp-format';

import TopicSelect from '../components/TopicSelect';
import NoteSettings from './NoteSettings';
import NoteShowAttachment from './NoteShowAttachment';
import NoteTip from './NoteTip';
import ShortVoiceNote from './ShortVoiceNote';
import styles from './index.less';
import MDStyles from './markdown.less';
import { isShortVoiceNote } from './utils';

interface IProps {
  className?: string;
  noteClassName?: string;
  detail: INote | INote_List;
  showAttachNumber?: number;
  origin?: 'list' | 'chat' | 'recall' | 'select' | 'selected' | 'homepage'; // list: 笔记列表，chat: 聊天中出现，recall: 产物召回，select: 多选框，selected: 选中的展示
  onClick?: (event: React.MouseEvent<HTMLDivElement>, params?: Record<string, any>) => void;
  deleteCb?: () => void;
  cancelSelectCb?: () => void;
  showTime?: boolean;
  showCTD?: boolean;
  highlightMap?: IhighLight;
  unread?: boolean;
  selected?: boolean;
  [key: string]: any;
}

function NoteItem(props: IProps): JSX.Element | null {
  const {
    className,
    detail,
    showAttachNumber = 1,
    origin = 'list',
    onClick: handleClick,
    deleteCb,
    cancelSelectCb,
    showTime = true,
    showCTD = false,
    highlightMap,
    noteClassName,
    unread = false,
    selected = false,
    ...others
  } = props;

  const { t } = useTranslation();
  const {
    title,
    content,
    attachments = [],
    createTime,
    createTimestamp,
    memoryType,
    generatedCtd
  } = detail;
  const _title = useMemo(() => {
    return highlightMap?.title || title;
  }, [highlightMap, title]);
  const _createTimestamp = createTime || createTimestamp;
  const [layoutSize] = useStorage('layoutSize');
  const [inMobile, setInMobile] = useState(layoutSize === LAYOUT_SIZE.SMALL);

  const showCreateTime = showTime && _createTimestamp;
  const showCTDItem = showCTD && !!generatedCtd?.content;
  const showResonanceItem = showCTD && !!generatedCtd?.content && !isProduct();

  const [process, setProcess] = useState<PROCESS_ENUM>();
  const [processLoading, setProcessLoading] = useState<boolean>(false);
  const { modal } = App.useApp();
  const addDeleteItemId = useNoteItemStore((state) => state.addDeleteItemId);
  const [mouseEnter, setMouseEnter] = useState<boolean>(false); // mouseEnter

  const { shadowMindId } = useGenius();
  const [isFirstResonanceBtn, setIsFirstResonanceBtn] = useState<boolean>(false);

  useEffect(() => {
    setInMobile(layoutSize === LAYOUT_SIZE.SMALL);
  }, [layoutSize]);

  useEffect(() => {
    if (origin === 'list') {
      const _detail = detail as INote_List;

      setProcess(_detail.process);
    }
  }, [detail, origin]);

  // 检查是否是第一个 resonanceBtn
  useLayoutEffect(() => {
    if (showCTD && generatedCtd?.content) {
      // 使用 setTimeout 确保 DOM 已经渲染完成
      setTimeout(() => {
        const allResonanceBtns = document.querySelectorAll('.resonanceBtn');

        if (allResonanceBtns.length > 0) {
          // 获取当前组件中的 resonanceBtn 元素
          const currentResonanceBtn = document.getElementById(`resonance-${detail.noteId}`);

          // 检查当前元素是否是第一个 resonanceBtn
          setIsFirstResonanceBtn(currentResonanceBtn === allResonanceBtns[0]);
        }
      }, 100);
    }
  }, [showCTD, generatedCtd, detail.noteId]);

  const _showContent = useMemo(() => {
    switch (memoryType) {
      case EMemoryType.TEXT:
        return highlightMap?.content || highlightMap?.snippet || content || '';
      case EMemoryType.PAGE:
        return highlightMap?.content || highlightMap?.snippet || content || '';

      case EMemoryType.IMAGE:
        return highlightMap?.content || highlightMap?.insight || highlightMap?.snippet || '';
      case EMemoryType.DOC:
      case EMemoryType.AUDIO:
      case EMemoryType.LINK:
        return (
          highlightMap?.content ||
          highlightMap?.['attachments.content'] ||
          highlightMap?.insight ||
          highlightMap?.snippet ||
          ''
        );
      default:
        return '';
    }
  }, [content, highlightMap, memoryType]);

  const attachmentEllipsis = attachments.length && attachments.length > showAttachNumber;

  const handleRelearn = useCallback(() => {
    const _detail = detail as INote_List;

    if (!_detail.noteId) {
      message.error(t('error.oops'));

      return;
    }

    setProcessLoading(true);
    relearnNote({ noteId: _detail.noteId, mindId: (_detail.mindId || shadowMindId)! })
      .then((res) => {
        if (res.data.code !== 0) {
          throw new Error(res.data.message || 'Failed to relearn.');
        }

        message.success('Relearning...');
      })
      .catch((err) => {
        if (err.name !== 'AxiosError') message.error(err.message);

        setProcessLoading(false);
      });
  }, [detail, shadowMindId]);

  const { run: runDelete } = useRequest(deleteNote, {
    manual: true,
    onSuccess: (res) => {
      if (res.data.code === 0) {
        message.success(t('common.deleteSuccess'));
        detail.noteId && addDeleteItemId(Number(detail.noteId));
        deleteCb?.();
      } else {
        message.error(res.data.message || t('common.deleteFail'));
      }
    },
    onError: (err) => {
      message.error(err.message || t('common.deleteFail'));
    }
  });

  if (isShortVoiceNote(detail)) {
    const { highlightMap: _highlightMap, ..._props } = props;

    return (
      <ShortVoiceNote
        {..._props}
        highlight={highlightMap?.content || highlightMap?.snippet || ''}
      />
    );
  }

  return (
    <div className={classNames('flex flex-col gap-1', className)}>
      {showCreateTime ? (
        <div className="!text-[12px] leading-5 text-[#b8ad9b]">
          {generateTimeString(_createTimestamp)}
        </div>
      ) : null}
      <div
        className={classNames(styles.container, noteClassName, 'group')}
        onClick={(e) => handleClick?.(e)}
        {...others}
        onMouseEnter={() => {
          setMouseEnter(true);
        }}
        onMouseLeave={() => {
          setMouseEnter(false);
        }}
      >
        {process === PROCESS_ENUM.undone ? (
          <div className={styles.topContainer}>
            <ExclamationCircleFilled
              className="text-[#FF7875] rounded-[50%] text-[18px] mr-3 flex-shrink-0"
              onClick={(e) => {
                e.stopPropagation();
              }}
            />
            <div className={styles.warningTitle}>{'Saved but not connected.'}</div>

            <div
              className=" text-primary-white rounded-[8px] text-[12px] leading-5 bg-[#FF7875] h-7 px-3 py-1 cursor-pointer space-x-1 transition-colors duration-300 hover:bg-[rgba(255,120,117,0.8)]"
              onClick={(e) => {
                e.stopPropagation();
                processLoading ? undefined : handleRelearn();
              }}
            >
              {processLoading ? <Loading3QuartersOutlined spin={true} /> : <ReloadOutlined />}
              <span>{processLoading ? 'Retrying' : 'Retry'}</span>
            </div>

            {origin === 'list' && (
              <Icon
                className={
                  'w-6 h-6 p-[5px] ml-3 text-primary-black text-[12px] border border-solid border-[#eee9e4] shadow-[0_4px_12px_0_rgba(194,158,107,0.08)] bg-primary-white rounded-[30px]'
                }
                component={DeleteSvg}
                onClick={(e) => {
                  e.stopPropagation();

                  modal.confirm({
                    title: t('common.sureToDelete'),
                    onOk: () => {
                      detail.noteId && ((detail as INote_List).mindId || shadowMindId)
                        ? runDelete({
                            noteId: detail.noteId,
                            mindId: ((detail as INote_List).mindId || shadowMindId)!
                          })
                        : message.error(t('error.oops'));
                    },
                    okText: t('common.delete'),
                    cancelText: t('common.cancel')
                  });
                }}
              />
            )}
          </div>
        ) : (
          <>
            {origin === 'list' && (
              <>
                {unread && (
                  <NoteTip
                    className={classNames(
                      'absolute top-[14px] ',
                      process === PROCESS_ENUM.doing || inMobile
                        ? 'ltr:right-[50px] rtl:left-[50px]'
                        : 'ltr:right-5 rtl:left-5 group-hover:ltr:right-[50px] group-hover:rtl:left-[50px]'
                    )}
                    isLoading={process === PROCESS_ENUM.doing ? true : false}
                  />
                )}
                {process === PROCESS_ENUM.doing ? (
                  <Icon
                    className={
                      'w-6 h-6 p-[5px] ml-3 text-primary-black text-[12px] border border-solid border-[#eee9e4] shadow-[0_4px_12px_0_rgba(194,158,107,0.08)] bg-primary-white absolute ltr:right-5 rtl:left-5 top-[14px] z-10 rounded-[30px]'
                    }
                    component={DeleteSvg}
                    onClick={(e) => {
                      e.stopPropagation();
                      modal.confirm({
                        title: t('common.sureToDelete'),
                        onOk: () => {
                          detail.noteId && ((detail as INote_List).mindId || shadowMindId)
                            ? runDelete({
                                noteId: detail.noteId,
                                mindId: ((detail as INote_List).mindId || shadowMindId)!
                              })
                            : message.error(t('error.oops'));
                        },
                        okText: t('common.delete'),
                        cancelText: t('common.cancel')
                      });
                    }}
                  />
                ) : (
                  <NoteSettings
                    className={classNames(
                      'absolute ltr:right-5 rtl:left-5 top-[14px] transition-all duration-200 z-10 invisible opacity-0 ',
                      inMobile
                        ? '!visible !opacity-100'
                        : 'group-hover:visible group-hover:opacity-100'
                    )}
                    detail={detail}
                    mindId={shadowMindId}
                    onDeleteCb={deleteCb}
                  />
                )}
              </>
            )}

            {origin === 'select' && detail.noteId && (
              <div className={styles.iconWrapper}>
                <Icon
                  className={classNames(styles.icon, { [styles.done]: selected })}
                  component={selected ? DoneIcon : UndoneIcon}
                />
              </div>
            )}

            {origin === 'selected' && detail.noteId && (
              <div
                className={styles.closeBtn}
                onClick={(e) => {
                  e.stopPropagation();
                  cancelSelectCb?.();
                }}
              >
                <Icon className={styles.icon} component={CloseSvg} />
              </div>
            )}

            {_title ? (
              <div className={styles.topContainer}>
                <HighLight className={styles.title}>{_title}</HighLight>
              </div>
            ) : null}

            {origin === 'select' && !_title ? (
              <div className={styles.topContainer}>
                <Icon className={'text-[20px]'} component={NoteSvg} />
              </div>
            ) : null}

            {process === PROCESS_ENUM.doing &&
            !_title &&
            ![undefined, EMemoryType.TEXT].includes(memoryType) ? (
              <div className={classNames(styles.topContainer)}>
                <div className={styles.doingTitle}>{t('memoryDetail.detailLoadingTip')}</div>
              </div>
            ) : null}
          </>
        )}

        {/* 有内容且类型为text或者page, 或者有高亮才显示正文 */}
        {_showContent ? (
          <div
            className={classNames(styles.content, {
              [styles.noAttachments]: !attachments.length
            })}
          >
            <SimplyMD className={MDStyles.markdown} content={_showContent} />
          </div>
        ) : null}

        {!!attachments.length && (
          <div className={styles.attachmentContainer}>
            <NoteShowAttachment
              attachments={attachments}
              className={styles.showAttachment}
              highlightTitle={highlightMap?.['attachments.ogiTitle'] || undefined}
              memoryType={memoryType}
              mouseEnter={mouseEnter}
            />
            {attachmentEllipsis &&
              attachments[0].metadata?.ogi !== undefined &&
              memoryType !== EMemoryType.IMAGE && (
                <div className={styles.ellipsisCount}>{`+${
                  attachments.length - showAttachNumber
                }`}</div>
              )}
          </div>
        )}

        {origin === 'list' && detail.noteId && (
          <div className="flex items-center">
            <TopicSelect
              sourceId={Number(detail.noteId)}
              topics={(detail as INote_List).smartTopics}
            />
            {(detail as INote_List).chatCount ? (
              <div className="ml-auto flex items-center gap-[2px] text-[#756C5D]">
                <Icon className="text-base" component={ChatEmptySvg} />
                <span className="text-xs leading-5">{(detail as INote_List).chatCount}</span>
              </div>
            ) : null}
          </div>
        )}
      </div>
      {showCTDItem ? (
        <div className={styles.CTDContainer}>
          <div className={styles.corner} />
          <div className={styles.contentContainer} onClick={(e) => handleClick?.(e)}>
            <img
              className="w-[22px] h-[22px] rounded-[50%] shrink-0"
              src="https://mindverseglobal.mindverse.com/front-img/crm/1737439207363/mebot-bio.png"
            />
            <div className="text-sm leading-6 text-[#5d5446] flex-1 line-clamp-1">
              {generatedCtd.content}
            </div>
          </div>
          {showResonanceItem ? (
            isFirstResonanceBtn ? (
              <div
                className={classNames(styles.iconContainer, 'resonanceBtn')}
                id={`resonance-${detail.noteId}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClick?.(e, { ctdMode: 'resonance' });
                }}
              >
                <Icon className="text-[24px] text-[#756C5D]" component={SensorSvg} />
              </div>
            ) : (
              <div
                className={classNames(styles.iconContainer, 'resonanceBtn')}
                id={`resonance-${detail.noteId}`}
                onClick={(e) => {
                  e.stopPropagation();
                  handleClick?.(e, { ctdMode: 'resonance' });
                }}
              >
                <Icon className="text-[24px] text-[#756C5D]" component={SensorSvg} />
              </div>
            )
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

export default NoteItem;
